import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Download } from "lucide-react";
import * as XLSX from "xlsx";

export default function PhysicalStockView() {
  const COMPANY_CODE = localStorage.getItem("companyCode");
  const DEPARTMENT =
    localStorage.getItem("currentSelectedDepartment") || "MAIN";
  // const COMPANY_CODE = localStorage.getItem("companyCode") || "-";
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    fetchPhysicalStockViewList();
  }, []);

  const fetchPhysicalStockViewList = async () => {
    setLoading(true);
    setError(null);

    try {
      let API_PATHS = localStorage.getItem("apiPaths")
        ? JSON.parse(localStorage.getItem("apiPaths"))
        : "-";
      const BASE_URL =
        API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";
      const response = await fetch(
        `${BASE_URL}/api/CRMDocListView/${COMPANY_CODE}/PHYSICALSTOCK/-/-/-/-/-/${DEPARTMENT}/1/50`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(Array.isArray(result) ? result : [result]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Fetch detail data for a specific physical stock entry
  const fetchPhysicalStockDetail = async (phyNo, deptNo) => {
    try {
      let API_PATHS = localStorage.getItem("apiPaths")
        ? JSON.parse(localStorage.getItem("apiPaths"))
        : "-";
      const BASE_URL =
        API_PATHS[0]?.API_CONFIG || "https://cubixweberp.com:213";

      // Replace this with your actual detail API endpoint
      const response = await fetch(
        `${BASE_URL}/api/CRMREPORTGENERATOR/${COMPANY_CODE}/PHYVARIATION/${phyNo}/${deptNo}/-/-/-/-/-`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return Array.isArray(result) ? result : [result];
    } catch (err) {
      console.error("Error fetching detail:", err);
      throw err;
    }
  };

  // Download Excel function - defined once, used by all rows
  // Using useCallback to memoize the function
  //   const downloadExcel = useCallback(async (transfer) => {
  //     try {
  //       setDownloadingId(transfer.phy_no);

  //       // Fetch detail data for this specific transfer
  //       const detailData = await fetchPhysicalStockDetail(transfer.phy_no, transfer.deptno);

  //       // Prepare data for Excel
  //       const excelData = detailData.map((item, index) => ({
  //         'S.No': index + 1,
  //         'Code': item.code || '',
  //         'Description': item.idesc || '',
  //         'System Qty': item.sys_qty || 0,
  //         'Physical Qty': item.phy_qty || 0,
  //         'Variation': item.variation || 0,
  //         'Cost': item.cost || 0,
  //         'Phy Value': item.phy_qty * item.cost || '',
  //         'Sys Value': item.sys_qty * item.cost || '',
  //         'Diff. Value': item.variation * item.cost || '',
  //       }));

  //       // Create worksheet
  //       const worksheet = XLSX.utils.json_to_sheet(excelData);

  //       // Set column widths
  //       const columnWidths = [
  //         { wch: 8 },  // S.No
  //         { wch: 20 }, // Code
  //         { wch: 40 }, // Description
  //         { wch: 15 }, // System Qty
  //         { wch: 15 }, // Physical Qty
  //         { wch: 15 }, // Variation
  //         { wch: 15 }, // Cost
  //         { wch: 15 }, // Phy Value
  //         { wch: 15 }, // Sys Value
  //         { wch: 15 }, // Diff. Value
  //       ];
  //       worksheet['!cols'] = columnWidths;

  //       // Create workbook
  //       const workbook = XLSX.utils.book_new();
  //       XLSX.utils.book_append_sheet(workbook, worksheet, 'Variation Report');

  //       // Generate filename
  //       const filename = `PhysicalStock_${transfer.phy_no}_${new Date().toISOString().split('T')[0]}.xlsx`;

  //       // Download file
  //       XLSX.writeFile(workbook, filename);
  //     } catch (err) {
  //       alert(`Error downloading Excel: ${err.message}`);
  //     } finally {
  //       setDownloadingId(null);
  //     }
  //   }, []); // Empty dependency array since it doesn't depend on any props/state

  const downloadExcel = useCallback(async (transfer) => {
    try {
      setDownloadingId(transfer.phy_no);

      // 1. Fetch the detail records
      const detailData = await fetchPhysicalStockDetail(
        transfer.phy_no,
        transfer.deptno,
      );

      // 2. Calculate Totals for the Header
      let totalSysValue = 0;
      let totalPhyValue = 0;
      let totalDiffValue = 0;

      // 3. Prepare the Table Data and accumulate totals
      const tableData = detailData.map((item, index) => {
        const sysVal = Number(item.sys_qty || 0) * Number(item.cost || 0);
        const phyVal = Number(item.phy_qty || 0) * Number(item.cost || 0);
        const diffVal = Number(item.variation || 0) * Number(item.cost || 0);

        totalSysValue += sysVal;
        totalPhyValue += phyVal;
        totalDiffValue += diffVal;

        return {
          "S.No": index + 1,
          "Item Code": item.code || "",
          Description: item.idesc || "",
          "System Qty": Number(item.sys_qty || 0),
          "Physical Qty": Number(item.phy_qty || 0),
          Variation: Number(item.variation || 0),
          Cost: Number(item.cost || 0).toFixed(2),
          "Phy Value": phyVal.toFixed(2),
          "Sys Value": sysVal.toFixed(2),
          "Diff. Value": diffVal.toFixed(2),
        };
      });

      // 3. Create Worksheet
      const worksheet = XLSX.utils.json_to_sheet([]);

      // 4. Define the Header Rows
      const headerInfo = [
        ["PHYSICAL STOCK VARIATION REPORT"],
        ["Doc Number:", transfer.phy_no, "", "Department:", transfer.deptno],
        [
          "Doc Date:",
          formatDate(transfer.Column1),
          "",
          "Location:",
          transfer.Location,
          "",
          "Location Desc",
          transfer.Locn_Desc,
        ],
        [], // Spacer row
        ["SUMMARY TOTALS"],
        [
          "Total Sys Value:",
          totalSysValue.toFixed(2),
          "",
          "Total Phy Value:",
          totalPhyValue.toFixed(2),
        ],
        ["Total Difference:", totalDiffValue.toFixed(2)],
        [], // Spacer before table
      ];

      // 5. Add Headers to sheet starting at A1
      XLSX.utils.sheet_add_aoa(worksheet, headerInfo, { origin: "A1" });

      // 6. Add Table Data starting at A6 (below headers)
      XLSX.utils.sheet_add_json(worksheet, tableData, {
        origin: "A10",
        skipHeader: false,
      });

      // 7. Configure Column Widths
      worksheet["!cols"] = [
        { wch: 15 },
        { wch: 15 },
        { wch: 40 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
      ];

      // 8. Generate and save the file
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Variation Report");

      const fileName = `Variation_${transfer.phy_no}_${transfer.deptno}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (err) {
      console.error(err);
      alert("Error generating Excel: " + err.message);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Physical Stock View
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {data.length} record{data.length !== 1 ? "s" : ""} found
              </p>
            </div>
            {/* <button
              onClick={fetchLocations}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button> */}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-16">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-3"></div>
              <p className="text-gray-600">Loading data...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 shadow-md">
            <p className="text-red-800 font-medium">Error loading data</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        )}

        {!loading && !error && data.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-500">No data found</p>
          </div>
        )}

        {!loading && !error && data.length > 0 && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PHY NO
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entered By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.map((transfer, index) => (
                    <tr
                      key={transfer.phy_no || index}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-blue-600">
                          {transfer.phy_no}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {formatDate(transfer.Column1)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {transfer.deptno}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {transfer.Locn_Desc || transfer.Location}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {transfer.user_id}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => downloadExcel(transfer)}
                          disabled={downloadingId === transfer.phy_no}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                        >
                          {downloadingId === transfer.phy_no ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <Download className="w-4 h-4" />
                              Download Variation Report
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
